import { BodyPrimary, BodySecondary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { ProTip } from '../ProTip/ProTip';
import { StepNumber } from '../StepNumber/StepNumber';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import FormattedMessageText from '@/components/Formatted/FormattedMessageText';
import type { Step } from '@/components/HowToWin/helpers/constants';

type Props = {
    reverse?: boolean;
    step: Step;
    variant: 'desktop' | 'mobile';
};

export const HowToWinStepCard = ({ reverse = false, step, variant }: Props) => {
    const { formatMessage } = useIntl();
    const isMobile = variant === 'mobile';

    if (isMobile) {
        return (
            <section
                className="mx-sm flex flex-col rounded-lg border border-border-secondary"
                data-testid={`howToWinStepCard-${step.number}-mobile`}
            >
                <div className="flex flex-col items-center gap-xs px-sm pt-md">
                    <StepNumber
                        mobile
                        number={step.number}
                    />
                    <div className="flex w-full flex-col items-center gap-2xs text-center">
                        <H2 className="text-text-primary">{formatMessage({ id: step.titleId })}</H2>
                        <BodySecondary className="text-text-secondary">
                            <FormattedMessageText id={step.descriptionId} />
                        </BodySecondary>
                    </div>
                </div>
                {/* Design: image breaks out of card padding for edge-to-edge display. -mx-xl pulls
                    the image out by xl on each side; w-[calc(...)] compensates so it spans the
                    full visual width of the card. max-w-none overrides any default img constraints. */}
                <img
                    alt={formatMessage({ id: step.imageAltId })}
                    className="-mx-xl w-[calc(100%+2*theme(spacing.xl))] max-w-none"
                    loading="lazy"
                    src={step.imageUrl}
                />
                <ProTip
                    messageId={step.proTipId}
                    variant="embedded"
                />
            </section>
        );
    }

    return (
        <section
            className={cn(
                'flex flex-col items-center gap-[159px] px-lg py-2xl mdMax:gap-xl mdMin:flex-row',
                'rounded-lg border border-border-secondary',
                {
                    'mdMin:flex-row-reverse': reverse,
                }
            )}
            data-testid={`howToWinStepCard-${step.number}-desktop`}
        >
            <div
                className={cn('flex max-w-[651px] flex-1 flex-col gap-md', {
                    'ml-[56px]': !reverse,
                    'mr-[56px]': reverse,
                })}
            >
                <StepNumber number={step.number} />
                <div className="flex flex-col">
                    <H2 className="mb-sm">{formatMessage({ id: step.titleId })}</H2>
                    <BodyPrimary className="mb-md">
                        <FormattedMessageText id={step.descriptionId} />
                    </BodyPrimary>
                    <ProTip messageId={step.proTipId} />
                </div>
            </div>
            <img
                alt={formatMessage({ id: step.imageAltId })}
                className="w-full max-w-[516px] flex-1 rounded-container"
                loading="lazy"
                src={step.imageUrl}
            />
        </section>
    );
};
